import {assertAndContinue} from 'Asserts';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {
  DisplayInfo,
  VisualizationSequenceSummary,
} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import * as sequtil from 'molbio/SeqUtil';
import {Point} from 'util/geometry/GeometryConstants';
import {assertNever} from 'util/Types';

import {
  PolymerPreviewOverlay,
  PolymerPreviewOverlayType,
  POLYMER_PREVIEW_OVERLAY_TYPE_TO_BG_COLOR,
  POLYMER_PREVIEW_OVERLAY_TYPE_TO_COLOR,
} from './PolymerPreviewConstants';

export function getDisplayInfos(sequence: VisualizationSequenceSummary): {
  startDisplayInfo: DisplayInfo;
  endDisplayInfo: DisplayInfo;
} {
  switch (sequence.strand) {
    case 1:
      return {
        startDisplayInfo: sequence.fivePrimeDisplayInfo,
        endDisplayInfo: sequence.threePrimeDisplayInfo,
      };
    case -1:
      return {
        endDisplayInfo: sequence.fivePrimeDisplayInfo,
        startDisplayInfo: sequence.threePrimeDisplayInfo,
      };
    default:
      throw new Error(`Invalid strand: ${assertNever(sequence.strand)}`);
  }
}

/**
 * Returns the index to render the arrow for a particular fragment.
 *
 * This function may return index == length, to indicate that the arrow should be at the very end
 * of the sequence (as opposed to the beginning).
 */
export function getIndexForArrow(
  start: number,
  end: number | null,
  strand: StrandOfDoubleStranded,
  length: number
): number {
  switch (strand) {
    case 1:
      return end != null ? sequtil.modelEndToRender(end, length) : start;
    case -1:
      return start;
    default:
      assertAndContinue(false, `Unexpected strand: ${assertNever(strand)}`);
      return start;
  }
}

/**
 * Gets the cartesian point for rendering at an index on a linear polymer visualization.
 * @param index residue index
 * @param length total residue length
 * @param leftPoint leftmost point where the linear axis rendering starts
 * @param rightPoint rightmost point where the linear axis rendering ends
 * @param yOffset how far from the axis in the vertical direction the point should be
 */
export function getLinearPointForIndex(
  index: number,
  length: number,
  leftPoint: Point,
  rightPoint: Point,
  yOffset: number = 0
): Point {
  const totalLength = rightPoint.x - leftPoint.x;
  return {x: leftPoint.x + (index / length) * totalLength, y: leftPoint.y + yOffset};
}

/**
 * Gets the parameters needed for rendering edit overlay visuals on polymer previews for the given overlay,
 * including the colors to show for the background color, solid fragment color, and boundary lines.
 *
 * Updates do not have a background color or boundary lines, and delete fragment visuals appear outside
 * of the axis. If a delete is less than 5% of the total sequence, there is no solid fragment visual
 */
export function getEditOverlayRenderParams(
  overlay: PolymerPreviewOverlay,
  seqLength: number
): {
  showFragmentOutside: boolean;
  fragmentColor: string | null;
  boundaryLineColor: string | null;
  backgroundColor: string | null;
} {
  const {start, end, type, color} = overlay;
  const length = sequtil.length(start, end, seqLength);
  const showFragmentOutside = type === PolymerPreviewOverlayType.DELETE;
  const showSolidFragment = !showFragmentOutside || length >= seqLength * 0.05;
  const showBoundaryLines = type !== PolymerPreviewOverlayType.UPDATE;
  const editColor = POLYMER_PREVIEW_OVERLAY_TYPE_TO_COLOR[type];
  const backgroundColor = POLYMER_PREVIEW_OVERLAY_TYPE_TO_BG_COLOR[type];

  return {
    showFragmentOutside,
    fragmentColor: showSolidFragment ? (color ?? editColor) : null,
    boundaryLineColor: showBoundaryLines ? editColor : null,
    backgroundColor,
  };
}
